import { DestroyRef, inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import {
    AuthController,
    CompanyController,
    CompanyInviteController,
    FeatureFlagController,
    NotificationController,
    SubscriptionController,
    UserController,
} from '@domain';
import { LinkController } from 'src/app/domain/controllers/link.controller';
import { I18nService } from 'src/app/shared/services/i18n.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Injectable({
    providedIn: 'root',
})
export class CoreService {
    // * [Injects]
    private _destroyRef = inject(DestroyRef);
    private _i18nService = inject(I18nService);
    private _themeService = inject(ThemeService);
    private _linkController = inject(LinkController);
    private _authController = inject(AuthController);
    private _userController = inject(UserController);
    private _companyController = inject(CompanyController);
    private _featureFlagController = inject(FeatureFlagController);
    private _subscriptionController = inject(SubscriptionController);
    private _notificationController = inject(NotificationController);
    private _companyInviteController = inject(CompanyInviteController);

    /**
     * Inicia todos os requests globais.
     *
     * A promessa devolvida resolve quando a primeira carga assenta, e é ela que
     * a tela de abertura espera (`SplashScreenComponent`). Cada request engole o
     * próprio erro: o que importa para a tela sair é que a rede tenha respondido,
     * não que ela tenha respondido bem. Sem sessão não há carga, e ela resolve na
     * hora.
     */
    public initAllGlobalRequests(): Promise<void> {
        this._i18nService.init();

        // * [Theme]
        // Fora do early return: as telas públicas (login, aceite de convite, preview de
        // contrato) também precisam do signal de tema sincronizado com o `data-theme` que
        // o script anti-FOUC do index.html já aplicou.
        this._themeService.init();

        if (!this._authController.isLoggedIn()) return Promise.resolve();

        // * [Pushers]
        this._authController.init(this._destroyRef);
        this._subscriptionController.init(this._destroyRef);
        this._notificationController.init(this._destroyRef);

        // * [Empresa atual]
        // Re-sincroniza a empresa atual com o backend (o GET /company devolve a
        // empresa do companyAccess marcado como `current`). Evita que o header e o
        // seletor de empresas mostrem empresas diferentes por cookie defasado.
        //
        // Roda SEMPRE que há sessão, e não só quando sobrou empresa no cookie: era
        // justamente quando o cookie não estava mais lá (expirado, ou descartado por
        // exceder o tamanho máximo) que a busca não acontecia, e aí a empresa ficava
        // ausente pelo resto da sessão — o header sem nome e sem logo.
        this._seedCompanyFromUser();

        const requests: Observable<unknown>[] = [
            this._companyController.syncCurrentCompany(),

            // * [Feature Flags]
            this._linkController.getAll(),
            this._featureFlagController.getAll(),

            // * [Notifications]
            this._notificationController.refreshUnreadCount(),

            // * [Convites recebidos]
            // Carregado no bootstrap para o menu lateral já mostrar o contador — é o único
            // sinal de que existe um convite esperando aceite na web.
            this._companyInviteController.loadPendingInvites(),
        ];

        return firstValueFrom(
            forkJoin(requests.map(request => request.pipe(catchError(() => of(null))))),
        ).then(() => undefined);
    }

    /**
     * Preenche a empresa com o vínculo que já está em memória (`companyAccesses.current`),
     * para o header não passar em branco no intervalo até o `syncCurrentCompany` responder.
     * Traz só id e nome — é o que o cookie do usuário guarda —, e o GET completa o resto.
     */
    private _seedCompanyFromUser(): void {
        if (this._companyController.getCurrentCompany()) return;

        const company = this._userController
            .getCurrentUser()
            ?.companyAccesses?.find(access => access.current)?.company;

        if (company?._id) this._companyController.setCurrentCompany(company);
    }
}
